import { logException, logMessage } from "@/analytics/sentry";

import { Comedian } from "./comedians";
import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";

export type NotificationRow =
  Database["public"]["Tables"]["web_notifications"]["Row"];
export type NotificationInsert =
  Database["public"]["Tables"]["web_notifications"]["Insert"];
export type NotificationUpdate =
  Database["public"]["Tables"]["web_notifications"]["Update"];
export type NotificationWithComedian = NotificationRow & {
  comedian: Pick<
    Comedian,
    "id" | "slug" | "display_name" | "display_name_short" | "about_img"
  > | null;
};

// TODO: remember to update this to support fetching more than 1000 notifications
// or add pagination
export async function getUserNotifications(
  supabase: SupabaseClient<Database>
): Promise<NotificationWithComedian[] | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error(
      "Supabase client must have an active session to get notifications"
    );
  }

  const { status, data, error } = await supabase
    .from("web_notifications")
    .select(
      `
        *,
        comedian:comedians!public_web_notifications_comedian_id_fkey!inner (
          id,
          slug,
          display_name,
          display_name_short,
          about_img
        )
      `
    )
    .eq("user_id", user.id)
    .order("related_timestamp", { ascending: false });

  if (error) {
    logException(error, {
      data: `Error fetching user notifications for user ${user.id}`,
    });
  } else if (status === 200) {
    if (data) {
      return data;
    }
  }
  logException(`Generic error fetching user notifications for user ${user.id}`);
  throw new Error(`Error getting notifications for user with id: ${user.id}`);
}

export async function upsertNotifications(
  supabase: SupabaseClient<Database>,
  payload: NotificationInsert[]
): Promise<boolean> {
  // Skip operation if there are no notifications to insert
  if (!payload.length) {
    return true;
  }

  const { count, error } = await supabase
    .from("web_notifications")
    .upsert(payload, {
      onConflict: "comedian_id, user_id, type, content_id",
      ignoreDuplicates: true,
      count: "exact",
    });

  if (error) {
    // This should error should not throw, we simply log it and move on. This is because occasionally the program might attempt to insert
    // duplicate notifications, however we're able to prevent that by using a Postgres constraint. In those cases the constraint will throw
    // but we just want to log this, and not block execution.
    logMessage(`Database error while attempting to upsert notifications`, {
      level: "warning",
      extra: {
        user_id: payload[0].user_id,
      },
      tags: {
        user_id: payload[0].user_id,
      },
    });
    return true;
  } else {
    // Same here we can't use this check since the count of affected rows will not match what the number of notifications that were supposed to be inserted, in those cases.
    if (count !== payload.length) {
      logMessage(
        `Error inserting notifications for user. Row count was ${count}.`,
        {
          level: "warning",
          extra: {
            user_id: payload[0].user_id,
          },
          tags: {
            user_id: payload[0].user_id,
          },
        }
      );
    }
    return true;
  }
}

export async function markAllAsRead(
  supabase: SupabaseClient<Database>
): Promise<boolean> {
  return await markAsRead(supabase);
}

export async function markAsRead(
  supabase: SupabaseClient<Database>,
  notificationsToUpdate?: Pick<NotificationUpdate, "notification_id">[]
): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error(
      "Supabase client must have an active session to mark notifications as read"
    );
  }

  let payload: NotificationUpdate[] = [];
  if (notificationsToUpdate && notificationsToUpdate.length > 0) {
    payload = notificationsToUpdate.map((notification) => ({
      notification_id: notification.notification_id,
      user_id: user.id,
      read: true,
    }));
  } else {
    const allUnreadNotifications = (
      await getUserNotifications(supabase)
    )?.filter((notification) => !notification.read);
    if (allUnreadNotifications && allUnreadNotifications.length > 0) {
      payload = allUnreadNotifications.map((notification) => ({
        notification_id: notification.notification_id,
        user_id: user.id,
        read: true,
      }));
    }
  }

  const { count, error } = await supabase
    .from("web_notifications")
    .upsert(payload, {
      ignoreDuplicates: false,
      count: "exact",
    });

  if (error) {
    logException(error, {
      data: `Error marking notifications as read for user ${user.id}`,
    });
    throw new Error(`Error marking notifications as read for user ${user.id}`);
  } else {
    if (count === payload.length) {
      return true;
    } else {
      const errMsg = `Error marking notifications as read for user ${user.id}. Row count was ${count}.`;
      logException(errMsg);
      throw new Error(errMsg);
    }
  }
}
